import { useState } from 'react'
import type { components } from '../../../api/generated/schema'
import {
  Banner,
  Button,
  Card,
  IconButton,
  Input,
  SectionLabel,
  SkillRow,
} from '../../../design/components'
import { useEnumCatalog, useSkillReference } from '../../reference/queries'
import { LOWEST_RUNG, skillLadder, skillRungs, type SkillRung } from '../types'
import type { CharacterEditing } from '../useCharacterEditing'
import { SHEET_SECTION_ID } from './sheetSections'
import styles from './sections.module.css'

type SkillsSection = components['schemas']['SkillsSectionDto']

/** The top of the Fate ladder. Nothing above Legendary exists to offer. */
const HIGHEST_RUNG = 8

/**
 * The FATE pyramid — rating rungs down the left, skills as pills, apex ember.
 *
 * Editing is *not* optimistic. Changing a skill runs the Fortitude cascade: the
 * server returns skills, stress and consequences together, and the point budget
 * and pyramid verdict with them. Guessing at any of that would mean showing
 * numbers that might be wrong, so the card waits.
 */
export function SkillsCard({
  skills,
  editing,
}: {
  skills: SkillsSection | undefined
  editing?: CharacterEditing | undefined
}) {
  const [open, setOpen] = useState(false)

  if (skills === undefined) return null

  const canEdit = editing?.canEdit === true
  const rungs = skillRungs(skills)
  const pyramidValid = skills.pyramid?.isValid ?? true
  const pyramidMessage = skills.pyramid?.message
  const pending =
    editing?.skillLevel.isPending === true ||
    editing?.removeSkill.isPending === true ||
    editing?.maxSkillLevel.isPending === true

  return (
    <Card
      id={SHEET_SECTION_ID.skills}
      variant="skills"
      label="Skills"
      hint={skills.poolOption !== undefined ? String(skills.poolOption) : undefined}
      action={
        canEdit && editing !== undefined ? (
          <IconButton
            label={open ? 'Done' : 'Adjust skills'}
            icon={open ? 'close' : 'edit'}
            onClick={() => setOpen((value) => !value)}
          />
        ) : undefined
      }
    >
      <div className={pending ? styles.pending : undefined}>
        {rungs.length === 0 ? (
          <p className={styles.empty}>No skills yet.</p>
        ) : (
          rungs.map((rung, index) => (
            <SkillRow
              key={rung.levelValue}
              rating={rung.levelValue}
              apex={index === 0}
              skills={rung.skills}
            />
          ))
        )}
      </div>

      <div className={styles.meta}>
        <Meta label="Total" value={skills.totalPoints ?? 0} />
        <Meta label="Spent" value={skills.spentPoints ?? 0} />
        <Meta
          label="Remaining"
          value={skills.remainingPoints ?? 0}
          warn={(skills.remainingPoints ?? 0) < 0}
        />
      </div>

      {!pyramidValid && pyramidMessage != null && (
        <div className={styles.pyramidWarning}>
          <Banner tone="warn">{pyramidMessage}</Banner>
        </div>
      )}

      {canEdit && editing !== undefined && open && (
        <SkillEditor skills={skills} editing={editing} />
      )}
    </Card>
  )
}

/**
 * One collapsible group per rung, from the character's cap down to Average (+1).
 *
 * The character's cap is what makes this possible: it says how many rungs there are, so the
 * editor can show the pyramid as a set of slots to fill rather than as a list of every skill
 * in the game with a level attached. Adding is an explicit act inside a rung, which is also
 * what keeps a skill to one place — there is no second row of it to disagree with the first.
 */
function SkillEditor({ skills, editing }: { skills: SkillsSection; editing: CharacterEditing }) {
  const ladder = skillLadder(skills)
  const levelNames = useSkillLevelNames()
  const cap = skills.maxLevelValue ?? HIGHEST_RUNG

  // Which rung each skill currently sits on. One entry per skill, which is the whole point:
  // moving one is a single write to the rung it lands on, not a remove and an add.
  const held = new Map<number, number>(
    (skills.skills ?? []).map((entry) => [entry.skillId ?? 0, entry.levelValue ?? 0]),
  )

  return (
    <div className={styles.skillEditor}>
      <PoolPicker skills={skills} editing={editing} />
      <MaxLevelPicker cap={cap} levelNames={levelNames} editing={editing} />

      {ladder.map((rung) => (
        <RungEditor
          key={rung.levelValue}
          rung={rung}
          label={levelNames.get(rung.levelValue) ?? ''}
          aboveCap={rung.levelValue > cap}
          held={held}
          editing={editing}
        />
      ))}
    </div>
  )
}

/**
 * The point budget: a named power level, and steppers for everything between them.
 *
 * Both exist because the pool is stored as the total itself, not as a choice among four
 * options — "Chest-Deep plus two" is a legal budget a table might agree on, and the named
 * levels are the shorthand for the common cases rather than the only ones allowed.
 */
function PoolPicker({ skills, editing }: { skills: SkillsSection; editing: CharacterEditing }) {
  const options = usePoolOptions()
  const total = skills.totalPoints ?? 0
  const pending = editing.skillPool.isPending || editing.adjustSkillPool.isPending

  // The server reports the pool as a named level; a nudged total lands between the names and
  // comes back as the bare number, which is the honest thing for the select to say too.
  const named = options.some((option) => option.points === total)

  return (
    <div className={styles.poolPicker}>
      <label>
        <SectionLabel tone="muted" micro>
          Skill points
        </SectionLabel>
        <select
          className={styles.levelSelect}
          aria-label="Skill point pool"
          value={named ? total : ''}
          disabled={pending}
          onChange={(event) => {
            const option = options.find((o) => String(o.points) === event.target.value)
            if (option !== undefined) editing.skillPool.mutate({ poolOption: option.name })
          }}
        >
          {!named && <option value="">{total} points</option>}
          {options.map((option) => (
            <option key={option.name} value={option.points}>
              {option.label} — {option.points}
            </option>
          ))}
        </select>
      </label>

      <div className={styles.stepperRow} style={{ marginTop: 'var(--sp-2)' }}>
        <button
          type="button"
          className={styles.stepper}
          aria-label="Remove a skill point"
          disabled={pending || total <= 0}
          onClick={() => editing.adjustSkillPool.mutate({ delta: -1 })}
        >
          −
        </button>
        <button
          type="button"
          className={styles.stepper}
          aria-label="Add a skill point"
          disabled={pending}
          onClick={() => editing.adjustSkillPool.mutate({ delta: 1 })}
        >
          +
        </button>
        <span className={styles.empty}>
          {skills.remainingPoints ?? 0} of {total} left to spend.
        </span>
      </div>
    </div>
  )
}

/**
 * The cap itself. Raising it adds a rung; lowering it takes one away, and the server refuses
 * if a skill is still standing on it — which skill to drop is the player's call.
 */
function MaxLevelPicker({
  cap,
  levelNames,
  editing,
}: {
  cap: number
  levelNames: Map<number, string>
  editing: CharacterEditing
}) {
  const rungs: number[] = []
  for (let value = HIGHEST_RUNG; value >= LOWEST_RUNG; value--) rungs.push(value)

  return (
    <label className={styles.capPicker}>
      <SectionLabel tone="muted" micro>
        Highest rung
      </SectionLabel>
      <select
        className={styles.levelSelect}
        aria-label="Highest skill rung"
        value={cap}
        disabled={editing.maxSkillLevel.isPending}
        onChange={(event) => editing.maxSkillLevel.mutate({ maxLevel: Number(event.target.value) })}
      >
        {rungs.map((value) => (
          <option key={value} value={value}>
            {signed(value)} {levelNames.get(value) ?? ''}
          </option>
        ))}
      </select>
    </label>
  )
}

/**
 * A single rung: the skills on it, and the way to put another there.
 *
 * Built on `<details>` so the open/closed announcement, keyboard operation and find-in-page
 * come from the platform. Every rung starts open — an empty one is a slot waiting to be
 * filled, which is the thing the editor exists to make obvious — and collapses on request.
 */
function RungEditor({
  rung,
  label,
  aboveCap,
  held,
  editing,
}: {
  rung: SkillRung
  label: string
  aboveCap: boolean
  held: Map<number, number>
  editing: CharacterEditing
}) {
  const [adding, setAdding] = useState(false)
  const count = rung.skills.length

  return (
    <details className={styles.rung} open>
      <summary className={styles.rungSummary}>
        <span className={styles.rungRating}>{signed(rung.levelValue)}</span>
        <span className={styles.rungName}>{label}</span>
        <span className={styles.rungCount}>
          {count === 0 ? 'empty' : count === 1 ? '1 skill' : `${count} skills`}
        </span>
      </summary>

      <div className={styles.rungBody}>
        {aboveCap && (
          <p className={styles.empty}>Above this character&rsquo;s cap — lower or remove these.</p>
        )}

        {rung.skills.map((skill) => (
          <div key={skill.id} className={styles.rungSkill}>
            <span className={styles.rungSkillName}>{skill.name}</span>
            <Button
              variant="danger"
              disabled={editing.removeSkill.isPending}
              onClick={() => editing.removeSkill.mutate({ skillId: skill.id })}
            >
              Remove
            </Button>
          </div>
        ))}

        {/* Nothing new may be put above the cap — the server would refuse it. */}
        {!aboveCap &&
          (adding ? (
            <SkillPicker
              level={rung.levelValue}
              held={held}
              editing={editing}
              onClose={() => setAdding(false)}
            />
          ) : (
            <div className={styles.actions}>
              <Button variant="outline" onClick={() => setAdding(true)}>
                Add a skill at {signed(rung.levelValue)}
              </Button>
            </div>
          ))}
      </div>
    </details>
  )
}

/**
 * The master list, narrowed to what can actually go on this rung.
 *
 * Skills the character already holds are still offered, marked with where they are now:
 * picking one *moves* it, because `PUT /skills/{skillId}` sets a level on the one row a skill
 * has. That is what enforces "a skill appears once" — there is no way to express two rungs.
 */
function SkillPicker({
  level,
  held,
  editing,
  onClose,
}: {
  level: number
  held: Map<number, number>
  editing: CharacterEditing
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const reference = useSkillReference()

  const needle = search.trim().toLowerCase()
  const rows = (reference.data ?? [])
    .map((skill) => ({
      id: skill.id ?? 0,
      name: skill.name ?? '',
      usages: skill.usages ?? '',
      at: held.get(skill.id ?? 0) ?? null,
    }))
    // A skill already on this rung has nowhere to move to.
    .filter((row) => row.at !== level)
    .filter((row) => needle === '' || row.name.toLowerCase().includes(needle))
    // Untaken skills first: adding is the common case, moving the exception.
    .sort((a, b) => Number(a.at !== null) - Number(b.at !== null) || a.name.localeCompare(b.name))

  return (
    <div className={styles.inlineForm}>
      <SectionLabel tone="ember" micro>
        Add a skill at {signed(level)}
      </SectionLabel>

      <Input
        label="Find a skill"
        type="search"
        placeholder="Start typing…"
        value={search}
        autoFocus
        onChange={(event) => setSearch(event.target.value)}
      />

      {reference.isPending && <p className={styles.empty}>Loading the skill list…</p>}
      {reference.isError && <p className={styles.empty}>Could not load the skill list.</p>}
      {reference.isSuccess && rows.length === 0 && (
        <p className={styles.empty}>
          {needle === '' ? 'Every skill is already here.' : `Nothing matches “${search.trim()}”.`}
        </p>
      )}

      {rows.slice(0, 12).map((row) => (
        <button
          key={row.id}
          type="button"
          className={styles.pickerRow}
          title={row.usages}
          disabled={editing.skillLevel.isPending}
          onClick={() =>
            editing.skillLevel.mutate({ skillId: row.id, level }, { onSuccess: onClose })
          }
        >
          <span className={styles.rungSkillName}>{row.name}</span>
          {row.at !== null && <span className={styles.pickerAt}>now at {signed(row.at)}</span>}
        </button>
      ))}

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={editing.skillLevel.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

/**
 * Ladder names by rung value, from the server's enum catalog rather than a table here —
 * "Superb" is a domain value, and the client hard-codes none of those.
 */
function useSkillLevelNames(): Map<number, string> {
  const catalog = useEnumCatalog()
  const members = catalog.data?.catalogs?.['skillLevels'] ?? []

  return new Map(members.map((member) => [member.score ?? member.value ?? 0, member.name ?? '']))
}

export interface PoolOption {
  /** The enum member name, which is what `PUT /skills/pool` takes. */
  name: string
  /** "Chest Deep" — the member name spaced out for reading. */
  label: string
  /** The total point budget the level grants; the catalog carries it as the member's score. */
  points: number
}

/** The named power levels, cheapest first. Same source, same reason as the ladder names. */
function usePoolOptions(): PoolOption[] {
  const catalog = useEnumCatalog()
  const members = catalog.data?.catalogs?.['skillPoolOptions'] ?? []

  return members
    .map((member) => ({
      name: member.name ?? '',
      label: spaced(member.name ?? ''),
      points: member.score ?? member.value ?? 0,
    }))
    .sort((a, b) => a.points - b.points)
}

/** "FeetInTheWater" → "Feet In The Water". The catalog ships member names, not prose. */
function spaced(name: string): string {
  return name.replace(/([a-z])([A-Z])/g, '$1 $2')
}

function signed(value: number): string {
  return value >= 0 ? `+${value}` : `${value}`
}

function Meta({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className={styles.metaItem}>
      <SectionLabel tone="muted" micro>
        {label}
      </SectionLabel>
      <span className={warn ? styles.metaValueWarn : styles.metaValue}>{value}</span>
    </div>
  )
}

export { Meta }
